import { DxAPIConnector } from "@/api";

import { Theme } from "@/components/views/CustomerManagement/Utils/Palette";
import { AvailableLanguages, EditingItem } from "@/types";
import { defineStore } from "pinia";
import { RouteLocationNormalized } from "vue-router";
import { COORDINATES, LOGIN, OWMAPPID as OWMAPPID_CONST, PAGES } from "./constants";
import { computed, ref, watch } from "vue";
import { DEFAULT_NEUTRAL_COLOR_SCHEME } from "@/themes";

export interface WLPreviewData {
  contract: string;
  companyName: string;
  productName: string;
  productLogo: string;
  companyLogo: string;
  favIcon: string;
  primaryColor: string;
  secondaryColor: string;
  primaryContrast: string;
  isFullyCustomized: boolean;
}

export interface WLCompany {
  legalName: string;
  mailSender: string;
  websiteTranslatable: Record<string, string>;
  imprintTranslatable: Record<string, string>;
  dataPrivacyTranslatable: Record<string, string>;
}

export const useGlobalConfigsStore = defineStore("globalConfigs", () => {
  /** @deprecated use direct import of `OWMAPPID` from constants instead of store */
  const OWMAPPID = OWMAPPID_CONST;
  /** @deprecated use direct import of `COORDINATES` from constants instead of store */
  const coordinates = COORDINATES;

  const decimals = ref(2);
  const isAuth = ref<undefined | boolean>();
  const isLoading = ref(true);
  const session = ref("");
  const translation = ref<object>({});
  const backlink = ref<null | RouteLocationNormalized>(null);
  const language = ref("en");
  const availableLanguages = ref<AvailableLanguages>({});
  const brand = ref(localStorage.getItem("prominent.brand") ?? "");
  const company = ref<WLCompany>({
    legalName: "",
    mailSender: "",
    websiteTranslatable: {},
    imprintTranslatable: {},
    dataPrivacyTranslatable: {},
  });
  const allowedMenuItems = ref<string[]>(JSON.parse(localStorage.getItem("prominent.allowedMenuItems") ?? "[]"));
  const editingItem = ref<Partial<EditingItem>>({}); // Global editing Variable. Determines, whether editing screen is shown or "normal" display of values
  const connector = ref<undefined | DxAPIConnector>(undefined);
  const wlPreviewData = ref<WLPreviewData>({
    contract: "",
    companyName: "",
    productName: "",
    productLogo: "",
    companyLogo: "",
    favIcon: "",
    primaryColor: "#01305c",
    secondaryColor: "#ea7b16",
    primaryContrast: "#FFFFFF",
    isFullyCustomized: false,
    ...JSON.parse(localStorage.getItem("prominent.wlPreview") ?? "{}"),
  });
  const _theme = ref<Theme>(JSON.parse(String(localStorage.getItem("prominent.theme"))) ?? DEFAULT_NEUTRAL_COLOR_SCHEME);
  const bannerMessage = ref("");

  const menuItemsByWL = computed(() => {
    return [...PAGES.filter((p) => allowedMenuItems.value.includes(p.path.replace("/", ""))), LOGIN]; // everyone has the option to log in!
  });

  const availableLanguageCodes = computed(() => {
    return Object.keys(availableLanguages.value);
  });

  function setLanguage(languageToSet: string) {
    const lang = languageToSet || languageToSet != null || languageToSet !== "null" ? languageToSet : "en";
    localStorage.setItem("prominent.user.language", lang); // keep it until refactoring replaces the localStorage dependency
    language.value = lang;
  }

  function setEditingItem(item: Partial<EditingItem>) {
    editingItem.value = item;
  }

  function removeEditingItem() {
    editingItem.value = {};
  }

  async function fetchBannerMessage() {
    const data = await connector.value?.general.getBannerMessage();
    bannerMessage.value = data ?? "";
  }

  function setWLTemplates() {
    const obj = new Object();
    Object.assign(
      obj,
      availableLanguageCodes.value.map((code) => ({ [code]: "" }))
    );
  }

  watch(
    () => brand.value,
    (b) => {
      localStorage.setItem("prominent.brand", b);
    }
  );

  watch(
    () => _theme.value,
    (t) => {
      localStorage.setItem("prominent.theme", JSON.stringify(t));
    }
  );

  watch(
    () => wlPreviewData.value,
    (t) => {
      localStorage.setItem("prominent.wlPreview", JSON.stringify(t));
    },
    { deep: true }
  );

  watch(
    () => allowedMenuItems.value,
    (t) => {
      localStorage.setItem("prominent.allowedMenuItems", JSON.stringify(t));
    },
    { deep: true }
  );

  watch(
    () => allowedMenuItems.value,
    (t) => {
      localStorage.setItem("prominent.allowedMenuItems", JSON.stringify(t));
    },
    { deep: true }
  );

  return {
    decimals,
    isAuth,
    isLoading,
    session,
    translation,
    backlink,
    language,
    availableLanguages,
    brand,
    company,
    allowedMenuItems,
    coordinates,
    OWMAPPID,
    editingItem,
    connector,
    wlPreviewData,
    _theme,
    bannerMessage,
    menuItemsByWL,
    getAvailableLanguageCodes: availableLanguageCodes,
    setLanguage,
    setEditingItem,
    removeEditingItem,
    fetchBannerMessage,
    setWLTemplates,
  };
});
